import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "@/hooks/use-toast";
import { usePrescriptionDraft } from "@/hooks/usePrescriptionDraft";
import { useAuth } from "@/context/AuthContext";
import { addPrescription, updatePrescription } from "@/lib/api";
import { PrescriptionDataTransformer, PrescriptionFormData } from "@/types/prescription";
import { parseAllergies, checkAllergies } from "@/lib/allergyCheck";
import { implausibleVitals } from "@/lib/vitalLimits";

// ─── Schema ───────────────────────────────────────────────────────────────────

const schema = z.object({
    id:          z.string().optional(),
    patient_id:  z.string().min(1, "Patient ID is required"),
    doctor_id:   z.string(),
    clinic_id:   z.string(),
    date:        z.string().min(1, "Date is required"),
    diagnosis:   z.string(),
    examination: z.string(),
    quick_notes: z.string(),
    advice:      z.string(),

    vitals: z.object({
        bp:      z.string().optional(),
        pulse:   z.string().optional(),
        spo2:    z.string().optional(),
        weight:  z.string().optional(),
        temp:    z.string().optional(),
        edema:   z.string().optional(),
        pallor:  z.string().optional(),
        icterus: z.string().optional(),
    }),

    complaints: z.array(
        z.object({
            complaint: z.string(),
            frequency: z.string().optional(),
            severity:  z.string().optional(),
            duration:  z.string().optional(),
            date:      z.string().optional(),
        })
    ),

    investigations: z.array(z.string()),

    medicines: z.array(
        z.object({
            drug_id:      z.number().nullable().optional(),
            brand_name:   z.string().optional(),
            generic_name: z.string().optional(),
            product:      z.string().optional(),
            dosage:       z.string().optional(),
            taken_when:   z.string().optional(),
            route:        z.string().optional(),
            duration:     z.string().optional(),
            quantity:     z.union([z.string(), z.number()]).optional(),
            notes:        z.string().optional(),
            // Drug meta — retained in form state for dosage option resolution.
            // Stripped from API payload by PrescriptionDataTransformer.toApiPayload().
            _drug_form:   z.string().optional(),
            _drug_brand:  z.string().optional(),
        })
    ),

    next_visit: z.object({
        date:  z.string().optional(),
        time:  z.string().optional(),
        notes: z.string().optional(),
    }).optional(),

    custom_fields: z.object({
        custom_1: z.string().optional(),
        custom_2: z.string().optional(),
        custom_3: z.string().optional(),
    }).optional(),
}).transform((data) => ({
    ...data,
    complaints:     data.complaints?.filter((c) => c.complaint?.trim())          || [],
    investigations: data.investigations?.filter((i) => i?.trim())                || [],
    // Strip drug meta before the transformed value leaves the schema
    medicines: data.medicines
        ?.filter((m) => m.product?.trim())
        .map(({ _drug_form, _drug_brand, ...rest }) => rest) || [],
}));

type FormDataType = z.infer<typeof schema>;

// ─── Types ────────────────────────────────────────────────────────────────────

interface UsePrescriptionFormOptions {
    mode?: "create" | "edit";
    patientId: string;
    initialData?: Partial<PrescriptionFormData>;
    // Receives the saved prescription's id — used to flush any documents
    // staged before the prescription existed (see AttachmentsSection /
    // PendingAttachments). Existing callers that ignore the argument keep
    // working unchanged.
    onSaved?: (prescriptionId?: string) => void;
    onCancel?: () => void;
    // Patient's recorded allergies (free text); saving a medicine that matches one needs confirmation
    patientAllergies?: string | null;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const usePrescriptionForm = ({
                                        mode = "create",
                                        patientId,
                                        initialData,
                                        onSaved,
                                        onCancel,
                                        patientAllergies,
                                    }: UsePrescriptionFormOptions) => {
    const { currentUser } = useAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasChanges, setHasChanges]     = useState(false);
    // Severe / contraindicated interaction lines, kept current by MedicineListForm
    const interactionAlertsRef            = useRef<string[]>([]);

    // Stable refs so callbacks don't re-create when parent passes new inline functions
    const onSavedRef  = useRef(onSaved);
    const onCancelRef = useRef(onCancel);
    useEffect(() => { onSavedRef.current  = onSaved;  }, [onSaved]);
    useEffect(() => { onCancelRef.current = onCancel; }, [onCancel]);

    // Default values
    // A copy (create + initialData) is new, unsaved content, so it counts as changed from the start
    // (the copied values are the form's baseline, so isDirty alone would leave Save disabled).
    const isCopy = mode === "create" && !!initialData;
    const defaultValues = useMemo((): PrescriptionFormData => {
        if (initialData) {
            return PrescriptionDataTransformer.toFormData(initialData as any, patientId);
        }
        return PrescriptionDataTransformer.createEmpty(
            patientId,
            currentUser?.doctor_id || "",
            currentUser?.clinic_id || ""
        );
    }, [mode, initialData, patientId, currentUser?.doctor_id, currentUser?.clinic_id]);

    const form = useForm<PrescriptionFormData>({
        resolver: zodResolver(schema) as any,
        defaultValues,
        mode: "onChange",
    });

    const { getValues, formState: { errors, isDirty }, watch } = form;
    const watchedValues = watch();

    // Track changes
    const changed = isDirty || isCopy;
    useEffect(() => {
        setHasChanges(changed);
    }, [changed]);

    // Unsaved work survives leaving the pad (restored on return). The form starts from the saved
    // prescription (defaultValues); a restore is the only reset, so the text boxes show the draft.
    const { discardDraft } = usePrescriptionDraft({
        form,
        doctorId: currentUser?.doctor_id,
        mode,
        patientId,
        rxId: (initialData as any)?.id,
        initialUpdatedAt: (initialData as any)?.updated_at,
        isCopy,
    });

    // Meaningful content check.
    // Computed from the live values to a plain boolean: the watchedValues object itself must not
    // reach any dependency list (watch() returns a new object every render, so submitForm would be
    // recreated on every keystroke and the onFormReady effect in PrescriptionForm would loop).
    // The boolean only changes when content appears or disappears. Needed because a copied Rx counts
    // as changed from the start, so clearing everything must switch Save off by content, not by isDirty.
    const values = watchedValues;
    const hasValidChanges = changed && !!(
        values.complaints?.some((c) => typeof c.complaint === "string" && c.complaint.trim()) ||
        (typeof values.diagnosis === "string" && !!values.diagnosis.trim()) ||
        (typeof values.examination === "string" && !!values.examination.trim()) ||
        values.medicines?.some((m) => (m.brand_name || m.product)?.trim()) ||
        values.investigations?.some((i) => typeof i === "string" && i.trim()) ||
        (typeof values.advice === "string" && !!values.advice.trim()) ||
        (typeof values.quick_notes === "string" && !!values.quick_notes.trim()) ||
        Object.values(values.vitals || {}).some((v) => v && v !== "None") ||
        Object.values(values.custom_fields || {}).some((v) => typeof v === "string" && v.trim())
    );

    // Submit
    const handleSubmit = useCallback(
        async (data: PrescriptionFormData) => {
            if (!hasValidChanges && mode === "create") {
                toast({
                    title: "No Content",
                    description: "Please add some content before saving",
                    variant: "destructive",
                });
                return;
            }

            // Last line of defence: a medicine the patient is recorded as allergic to needs an explicit override
            const allergyTerms = parseAllergies(patientAllergies);
            const allergyErrors = (data.medicines ?? []).flatMap(m =>
                checkAllergies(allergyTerms, { composition: m.generic_name, product: m.product || m.brand_name })
                    .filter(h => h.level === "error")
                    .map(h => `• ${m.product || m.brand_name}: ${h.text}`)
            );
            // Severe / contraindicated interactions, found by MedicineListForm
            const interactionAlerts = interactionAlertsRef.current;
            // A medicine without a duration prints "—": the pharmacist and the patient don't know how long to take it
            const noDuration = (data.medicines ?? [])
                .filter(m => (m.product || m.brand_name)?.trim() && !m.duration?.trim())
                .map(m => `• ${m.product || m.brand_name}`);
            // Readings no patient can have are usually a typing slip (13085 for 130/85)
            const oddVitals = implausibleVitals(data.vitals);
            const sections = [
                oddVitals.length ? `Check these vitals\n${oddVitals.join("\n")}` : "",
                allergyErrors.length ? `Allergy warning\n${allergyErrors.join("\n")}` : "",
                interactionAlerts.length ? `Drug interactions\n${interactionAlerts.join("\n")}` : "",
                noDuration.length ? `No duration given\n${noDuration.join("\n")}` : "",
            ].filter(Boolean);
            if (sections.length && !window.confirm(`${sections.join("\n\n")}\n\nSave this prescription anyway?`)) {
                return;
            }

            setIsSubmitting(true);
            try {
                const prescriptionData = PrescriptionDataTransformer.toApiPayload(data, mode === "edit");

                let savedId: string | undefined;
                if (mode === "edit" && data.id) {
                    await updatePrescription({ ...prescriptionData, id: data.id });
                    savedId = data.id;
                    toast({ title: "Updated Successfully", description: "Prescription has been updated" });
                } else {
                    const created = await addPrescription(prescriptionData);
                    savedId = created?.id;
                    toast({ title: "Saved Successfully", description: "New prescription has been created" });
                }

                discardDraft();
                onSavedRef.current?.(savedId);
            } catch (error: any) {
                console.error("Error saving prescription:", error);
                toast({
                    title: "Error",
                    description: error?.message || "Failed to save prescription. Please try again.",
                    variant: "destructive",
                });
            } finally {
                setIsSubmitting(false);
            }
        },
        [hasValidChanges, mode, patientAllergies, discardDraft]
    );

    // Cancel with unsaved changes guard
    const handleCancel = useCallback(() => {
        if (hasChanges) {
            const confirmed = window.confirm("You have unsaved changes. Are you sure you want to cancel?");
            if (!confirmed) return;
        }
        discardDraft();
        onCancelRef.current?.();
    }, [hasChanges, discardDraft]);

    const submitForm = useCallback(() => {
        form.handleSubmit(handleSubmit)();
    }, [form.handleSubmit, handleSubmit]);

    return {
        form,
        isSubmitting,
        hasChanges,
        hasValidChanges,
        errors,
        isDirty,
        submitForm,
        handleCancel,
        watchedValues,
        getValues,
        mode,
        patientId,
        interactionAlertsRef,
    };
};